import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Mail, MessageCircle, Sparkles, CheckCircle2, AlertCircle, Save, Lock, Send } from "lucide-react";
import { toast } from "sonner";

const empty = {
  email_provider: "", email_smtp_host: "", email_smtp_port: 587,
  email_username: "", email_password: "", email_from_name: "",
  whatsapp_provider: "", twilio_sid: "", twilio_auth_token: "", twilio_from: "",
  meta_phone_id: "", meta_access_token: "",
  claude_api_key: "",
};

export default function Integrations() {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState({ email: false, whatsapp: false, claude: false });
  const [saving, setSaving] = useState(false);
  const [setFlags, setSetFlags] = useState({});

  async function load() {
    const r = await api.get("/integrations");
    setForm({ ...empty, ...r.data, email_password: "", twilio_auth_token: "", meta_access_token: "", claude_api_key: "" });
    setStatus(r.data.status || {});
    setSetFlags({
      email_password: r.data.email_password_set,
      twilio_auth_token: r.data.twilio_auth_token_set,
      meta_access_token: r.data.meta_access_token_set,
      claude_api_key: r.data.claude_api_key_set,
    });
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form };
      // Don't send blank secrets — backend keeps existing
      await api.put("/integrations", payload);
      toast.success("Integrations saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function sendTest(channel) {
    const to = prompt(channel === "email"
      ? "Send a test email TO this address:"
      : "Send a WhatsApp test TO this number (e.g. +919999999999):");
    if (!to) return;
    try {
      const r = await api.post("/integrations/test", { channel, to });
      if (r.data.simulated) toast.info("Provider not configured — simulated send. Save credentials first.");
      else if (r.data.ok) toast.success(`Sent! Detail: ${r.data.detail}`);
      else toast.error(`Failed: ${r.data.detail}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    }
  }

  function presetEmail(v) {
    if (v === "gmail") setForm({ ...form, email_provider: "gmail", email_smtp_host: "smtp.gmail.com", email_smtp_port: 587 });
    else if (v === "o365") setForm({ ...form, email_provider: "o365", email_smtp_host: "smtp.office365.com", email_smtp_port: 587 });
    else setForm({ ...form, email_provider: v });
  }

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-4xl">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-sky-600 font-bold">Connect</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl text-slate-900 mt-1">Integrations</h1>
        <p className="text-slate-500 mt-1">Connect Email, WhatsApp, and (optionally) your own Claude API key. Sending is simulated until credentials are filled in.</p>
      </header>

      <div className="grid sm:grid-cols-3 gap-3">
        <StatusBadge icon={Mail} label="Email" ok={status.email}/>
        <StatusBadge icon={MessageCircle} label="WhatsApp" ok={status.whatsapp}/>
        <StatusBadge icon={Sparkles} label="Claude AI (BYO key)" ok={status.claude}/>
      </div>

      <Tabs defaultValue="email">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="email" data-testid="integ-tab-email">Email</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="integ-tab-whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="claude" data-testid="integ-tab-claude">Claude AI</TabsTrigger>
        </TabsList>

        <TabsContent value="email" className="mt-4">
          <Card className="border-slate-200 rounded-2xl"><CardContent className="p-6 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Provider</Label>
              <Select value={form.email_provider || undefined} onValueChange={presetEmail}>
                <SelectTrigger data-testid="email-provider"><SelectValue placeholder="Choose provider"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gmail">Gmail (smtp.gmail.com)</SelectItem>
                  <SelectItem value="o365">Microsoft 365 / Outlook</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <FF label="SMTP Host" v={form.email_smtp_host} on={v=>setForm({...form, email_smtp_host:v})}/>
              <FF label="SMTP Port" v={form.email_smtp_port} on={v=>setForm({...form, email_smtp_port: parseInt(v||"587")})}/>
              <FF label="Username (full email)" v={form.email_username} on={v=>setForm({...form, email_username:v})} tid="email-user"/>
              <FF label="App Password" v={form.email_password} on={v=>setForm({...form, email_password:v})} type="password" placeholder={setFlags.email_password ? "•••• already saved" : "16-char Gmail/O365 app password"} tid="email-pwd"/>
              <FF label="From Name" v={form.email_from_name} on={v=>setForm({...form, email_from_name:v})}/>
            </div>
            <Hint>
              <strong>Gmail:</strong> turn on 2FA → Google Account → App passwords → create one named "MedLearn". Use that 16-char password here.<br/>
              <strong>O365 / Outlook:</strong> turn on MFA → security info → app password (or use an OAuth-enabled mailbox).
            </Hint>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={()=>sendTest("email")} className="rounded-xl" data-testid="email-test-btn">
                <Send className="h-4 w-4 mr-1.5"/>Send test email
              </Button>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="whatsapp" className="mt-4">
          <Card className="border-slate-200 rounded-2xl"><CardContent className="p-6 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Provider</Label>
              <Select value={form.whatsapp_provider || undefined} onValueChange={v=>setForm({...form, whatsapp_provider:v})}>
                <SelectTrigger data-testid="wa-provider"><SelectValue placeholder="Choose provider"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="twilio">Twilio</SelectItem>
                  <SelectItem value="meta">Meta WhatsApp Business API</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.whatsapp_provider === "twilio" && (
              <div className="grid sm:grid-cols-2 gap-3">
                <FF label="Account SID" v={form.twilio_sid} on={v=>setForm({...form, twilio_sid:v})} tid="twilio-sid"/>
                <FF label="Auth Token" v={form.twilio_auth_token} on={v=>setForm({...form, twilio_auth_token:v})} type="password" placeholder={setFlags.twilio_auth_token ? "•••• already saved" : ""} tid="twilio-token"/>
                <FF label="From (whatsapp:+91…)" v={form.twilio_from} on={v=>setForm({...form, twilio_from:v})} tid="twilio-from"/>
              </div>
            )}
            {form.whatsapp_provider === "meta" && (
              <div className="grid sm:grid-cols-2 gap-3">
                <FF label="Phone Number ID" v={form.meta_phone_id} on={v=>setForm({...form, meta_phone_id:v})} tid="meta-phone"/>
                <FF label="Access Token" v={form.meta_access_token} on={v=>setForm({...form, meta_access_token:v})} type="password" placeholder={setFlags.meta_access_token ? "•••• already saved" : ""} tid="meta-token"/>
              </div>
            )}
            <Hint>
              Sandbox numbers (Twilio) work for testing. For production WhatsApp templated messaging via Meta, your Business account needs to be approved (typically 1-3 days).
            </Hint>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={()=>sendTest("whatsapp")} className="rounded-xl" data-testid="wa-test-btn">
                <Send className="h-4 w-4 mr-1.5"/>Send test WhatsApp
              </Button>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="claude" className="mt-4">
          <Card className="border-slate-200 rounded-2xl"><CardContent className="p-6 space-y-4">
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-sm">
              <Lock className="h-4 w-4 inline mr-1.5 text-slate-500"/>
              By default we use the <strong>Emergent Universal Key</strong> (credits billed to your Emergent account). To use your own Anthropic Pro key, paste it below — it will override the default for AI quiz generation.
            </div>
            <FF label="Anthropic API Key (sk-ant-…)" v={form.claude_api_key} on={v=>setForm({...form, claude_api_key:v})} type="password"
              placeholder={setFlags.claude_api_key ? "•••• already saved" : "sk-ant-XXXX-XXXX-XXXX"} tid="claude-key"/>
            <Hint>Get a key at <a href="https://console.anthropic.com/" target="_blank" rel="noreferrer" className="text-sky-600 font-bold">console.anthropic.com</a></Hint>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-sky-500 hover:bg-sky-600" data-testid="integ-save"><Save className="h-4 w-4 mr-1.5"/>{saving ? "Saving…" : "Save Configuration"}</Button>
      </div>
    </div>
  );
}

function StatusBadge({ icon: Icon, label, ok }) {
  return (
    <Card className="border-slate-200 rounded-2xl">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`h-10 w-10 rounded-xl grid place-items-center ${ok ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}><Icon className="h-5 w-5"/></div>
        <div className="flex-1">
          <div className="font-bold text-sm">{label}</div>
          <Badge variant="secondary" className={ok ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}>
            {ok ? <><CheckCircle2 className="h-3 w-3 mr-1"/>Configured</> : <><AlertCircle className="h-3 w-3 mr-1"/>Not configured</>}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}
function FF({ label, v, on, type = "text", placeholder, tid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      <Input value={v} onChange={e=>on(e.target.value)} type={type} placeholder={placeholder} data-testid={tid}/>
    </div>
  );
}
function Hint({ children }) {
  return <div className="text-xs text-slate-500 p-3 rounded-xl bg-amber-50 border border-amber-200">{children}</div>;
}
